import { useCallback, useMemo } from 'react';
import { toValueArray } from './useSelectState.helpers';
import type { SelectChangeHandler } from './useSelectState.types';

interface MultipleParams {
  multiple: boolean;
  multipleValues?: string[];
  multipleSummary?: (count: number) => string;
  getDisplayNameForValue: (optionValue: string) => string;
  onChange: SelectChangeHandler;
  name: string;
}

/**
 * Builds the display string for multiple-selection mode: joins labels with
 * commas, or falls back to a summary string when more than two are selected.
 *
 * @param multiple Whether multiple-selection mode is active.
 * @param values The currently selected option values.
 * @param getDisplay Resolver from option value to display label.
 * @param summary Optional formatter used when more than two are selected.
 * @returns The display string, empty when nothing is selected.
 */
function buildMultiDisplay(
  multiple: boolean, values: string[],
  getDisplay: (v: string) => string, summary?: (n: number) => string,
): string {
  if (!multiple || values.length === 0) return '';
  const labels = values.map(getDisplay);
  if (labels.length > 2 && summary) return summary(labels.length);
  return labels.join(', ');
}

/**
 * Encapsulates multiple-selection state: the normalized selected values array,
 * the toggle handler that adds/removes a value, and the display summary string.
 *
 * @param params Multiple-mode config, values, resolver, and change callback.
 * @returns `{ selectedValues, toggleValue, multiDisplayValue }`.
 */
export function useSelectMultiple({
  multiple,
  multipleValues,
  multipleSummary,
  getDisplayNameForValue,
  onChange,
  name,
}: MultipleParams) {
  const selectedValues = useMemo(() => toValueArray(multipleValues), [multipleValues]);

  const toggleValue = useCallback(
    (optionValue: string) => {
      const next = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(next, name);
    },
    [selectedValues, onChange, name],
  );

  const multiDisplayValue = useMemo(
    () => buildMultiDisplay(multiple, selectedValues, getDisplayNameForValue, multipleSummary),
    [multiple, selectedValues, getDisplayNameForValue, multipleSummary],
  );

  return { selectedValues, toggleValue, multiDisplayValue };
}
